import { getCanonicalMatchResult } from "./canonicalMatchResult";
import { REPORT_SEMANTIC_TYPES, tryResolveReportSemantics } from "./reportSemantics";

const TERMINAL = new Set(["confirmed", "finished", "cancelled", "void"]);

const isInvalid = (match) => {
  const reason = String(match?.winReason || match?.resolutionReason || "").toUpperCase();
  return match?.invalidated === true ||
    match?.seriesLegOutcome === "INVALID" ||
    String(match?.checkInResolution || "").toUpperCase() === "DOUBLE_NO_SHOW" ||
    reason === "ADMIN_REFUND";
};
const isForfeit = (match) => {
  const reason = String(match?.winReason || match?.resolutionReason || "").toUpperCase();
  const reportSemantics = tryResolveReportSemantics(match?.report || {});
  const checkInResolution = String(match?.checkInResolution || "").toUpperCase();
  return reportSemantics?.semanticType === REPORT_SEMANTIC_TYPES.RAGE_QUIT || reason.includes("RAGE_QUIT") || reason.includes("CHECK_IN_NO_SHOW") || checkInResolution === "CHECK_IN_NO_SHOW";
};
const isSeriesCheckInNoShowForfeit = (match, scheduledRegularLegCount) => {
  if (String(match?.status || "").toLowerCase() !== "confirmed" || String(match?.checkInResolution || "").toUpperCase() !== "CHECK_IN_NO_SHOW") return false;
  if (match?.seriesMatchRole === "decision_match") return true;
  const configuredRegularLegCount = Math.max(1, Number(scheduledRegularLegCount || 1));
  return Number(match?.legNumber || 1) >= configuredRegularLegCount;
};

export function getTournamentSeriesState({ matches = [], tieId = "" } = {}) {
  const id = String(tieId || "");
  const rawSeriesMatches = (matches || []).filter((match) => String(match?.tieId || "") === id);
  if (!id || rawSeriesMatches.length === 0) return null;

  const activeVersion = Math.max(...rawSeriesMatches.map((match) => Number(match?.seriesVersion || 1)));
  const activeMatches = rawSeriesMatches.filter((match) => Number(match?.seriesVersion || 1) === activeVersion);
  const regularLegs = activeMatches
    .filter((match) => match?.seriesMatchRole !== "decision_match")
    .sort((a, b) => Number(a?.legNumber || 0) - Number(b?.legNumber || 0));
  const decisionMatch = activeMatches.find((match) => match?.seriesMatchRole === "decision_match" && !isInvalid(match)) ||
    activeMatches.find((match) => match?.seriesMatchRole === "decision_match" && ["cancelled", "void", "finished"].includes(String(match?.status || "").toLowerCase())) ||
    activeMatches.find((match) => match?.seriesMatchRole === "decision_match") || null;
  const scheduledRegularLegCount = Math.max(1, ...activeMatches.map((match) => Number(match?.scheduledRegularLegCount || 1)));
  const explicitPlayer1Uid = activeMatches.find((match) => match?.seriesPlayer1Uid)?.seriesPlayer1Uid || null;
  const explicitPlayer2Uid = activeMatches.find((match) => match?.seriesPlayer2Uid)?.seriesPlayer2Uid || null;
  const completeCanonicalLeg = regularLegs.find((match) => match?.player1Uid && match?.player2Uid && match.player1Uid !== match.player2Uid) || null;
  const player1Uid = explicitPlayer1Uid || completeCanonicalLeg?.player1Uid || null;
  const player2Uid = explicitPlayer2Uid || completeCanonicalLeg?.player2Uid || null;
  if (!player1Uid || !player2Uid || player1Uid === player2Uid) return null;

  let player1Goals = 0;
  let player2Goals = 0;
  let terminalLegCount = 0;
  let validLegCount = 0;
  let forfeit = null;
  for (const leg of regularLegs) {
    if (TERMINAL.has(String(leg?.status || "").toLowerCase())) terminalLegCount += 1;
    const isWholeSeriesForfeit = isForfeit(leg) && (
      String(leg?.checkInResolution || "").toUpperCase() !== "CHECK_IN_NO_SHOW" ||
      isSeriesCheckInNoShowForfeit(leg, scheduledRegularLegCount)
    );
    if (isInvalid(leg)) continue;
    if (String(leg?.status || "").toLowerCase() !== "confirmed") continue;
    const score = getCanonicalMatchResult(leg);
    if (!score.hasScore) continue;
    if (leg.player1Uid === player1Uid) {
      player1Goals += score.scoreP1;
      player2Goals += score.scoreP2;
    } else {
      player1Goals += score.scoreP2;
      player2Goals += score.scoreP1;
    }
    validLegCount += 1;
    if (isWholeSeriesForfeit && leg?.winnerUid) {
      forfeit = leg;
      break;
    }
  }
  const aggregate = { player1Goals, player2Goals, terminalLegCount, validLegCount };

  if (forfeit) {
    return {
      tieId: id, resolved: true, resolutionType: "FORFEIT",
      winnerUid: forfeit.winnerUid, loserUid: forfeit.loserUid || (forfeit.winnerUid === player1Uid ? player2Uid : player1Uid),
      player1Uid, player2Uid, regularLegs, decisionMatch, decisiveMatch: forfeit, scheduledRegularLegCount, aggregate,
    };
  }

  if (terminalLegCount < scheduledRegularLegCount) {
    return { tieId: id, resolved: false, status: "regular_legs_pending", player1Uid, player2Uid, regularLegs, decisionMatch, scheduledRegularLegCount, aggregate };
  }
  if (validLegCount === 0) {
    return { tieId: id, resolved: true, resolutionType: "NO_VALID_REGULAR_LEG", winnerUid: null, loserUid: null, player1Uid, player2Uid, regularLegs, decisionMatch, scheduledRegularLegCount, aggregate };
  }
  if (player1Goals !== player2Goals) {
    const winnerUid = player1Goals > player2Goals ? player1Uid : player2Uid;
    return { tieId: id, resolved: true, resolutionType: "AGGREGATE", winnerUid, loserUid: winnerUid === player1Uid ? player2Uid : player1Uid, player1Uid, player2Uid, regularLegs, decisionMatch, scheduledRegularLegCount, aggregate };
  }
  if (decisionMatch && String(decisionMatch?.status || "").toLowerCase() === "confirmed" && decisionMatch?.winnerUid) {
    const decisionScore = isInvalid(decisionMatch) ? null : getCanonicalMatchResult(decisionMatch);
    const normalOrientation = String(decisionMatch?.player1Uid || "") === String(player1Uid || "");
    const finalAggregate = decisionScore?.hasScore
      ? {
          ...aggregate,
          player1Goals: aggregate.player1Goals + (normalOrientation ? decisionScore.scoreP1 : decisionScore.scoreP2),
          player2Goals: aggregate.player2Goals + (normalOrientation ? decisionScore.scoreP2 : decisionScore.scoreP1),
          validLegCount: aggregate.validLegCount + 1,
          terminalLegCount: aggregate.terminalLegCount + 1,
          tieBreakerIncluded: true,
        }
      : aggregate;
    return { tieId: id, resolved: true, resolutionType: isForfeit(decisionMatch) ? "FORFEIT" : "DECISION_MATCH", winnerUid: decisionMatch.winnerUid, loserUid: decisionMatch.loserUid || (decisionMatch.winnerUid === player1Uid ? player2Uid : player1Uid), player1Uid, player2Uid, regularLegs, decisionMatch, scheduledRegularLegCount, aggregate: finalAggregate };
  }
  if (decisionMatch && ["cancelled", "void", "finished"].includes(String(decisionMatch?.status || "").toLowerCase()) && !decisionMatch?.winnerUid) {
    return { tieId: id, resolved: true, resolutionType: "NO_VIABLE_WINNER", winnerUid: null, loserUid: null, player1Uid, player2Uid, regularLegs, decisionMatch, scheduledRegularLegCount, aggregate };
  }
  return { tieId: id, resolved: false, status: "decision_required", player1Uid, player2Uid, regularLegs, decisionMatch, scheduledRegularLegCount, aggregate };
}

export function getPlayerTournamentSeriesStates(matches = [], currentUid = "") {
  const uid = String(currentUid || "");
  const tieIds = [...new Set((matches || []).filter((match) =>
    match?.tieId && (String(match?.player1Uid || "") === uid || String(match?.player2Uid || "") === uid || String(match?.seriesPlayer1Uid || "") === uid || String(match?.seriesPlayer2Uid || "") === uid)
  ).map((match) => String(match.tieId)))];
  return tieIds.map((tieId) => getTournamentSeriesState({ matches, tieId })).filter(Boolean);
}

export function getTournamentSeriesDisqualification(seriesState) {
  if (!seriesState?.resolved) {
    return { disqualifiedUids: [], reason: null };
  }

  const playerUids = [seriesState.player1Uid, seriesState.player2Uid].filter(Boolean);
  if (seriesState.resolutionType === "NO_VALID_REGULAR_LEG") {
    return { disqualifiedUids: playerUids, reason: "NO_VALID_REGULAR_LEG" };
  }

  if (seriesState.resolutionType === "FORFEIT") {
    const decisiveRageQuitMatch = [
      ...(seriesState.regularLegs || []),
      seriesState.decisionMatch,
    ].filter(Boolean).find((match) => {
      const reason = String(match?.winReason || match?.resolutionReason || "").toUpperCase();
      const reportSemantics = tryResolveReportSemantics(match?.report || {});
      return String(match?.status || "").toLowerCase() === "confirmed" && (
        reportSemantics?.semanticType === REPORT_SEMANTIC_TYPES.RAGE_QUIT ||
        reason.includes("RAGE_QUIT")
      );
    });
    if (decisiveRageQuitMatch) {
      const loserUid = decisiveRageQuitMatch.report?.payload?.quitterUid || decisiveRageQuitMatch.loserUid || seriesState.loserUid;
      return loserUid
        ? { disqualifiedUids: [loserUid], reason: "RAGE_QUIT_FORFEIT" }
        : { disqualifiedUids: [], reason: null };
    }
  }

  const decisionMatch = seriesState.decisionMatch;
  if (
    seriesState.resolutionType === "NO_VIABLE_WINNER" &&
    String(decisionMatch?.checkInResolution || "").toUpperCase() === "DOUBLE_NO_SHOW"
  ) {
    return { disqualifiedUids: playerUids, reason: "DECISION_MATCH_DOUBLE_NO_SHOW" };
  }

  const decisiveNoShowMatch = [
    ...(seriesState.regularLegs || []),
    decisionMatch,
  ].filter(Boolean).find((match) =>
    String(match?.status || "").toLowerCase() === "confirmed" &&
    String(match?.checkInResolution || "").toUpperCase() === "CHECK_IN_NO_SHOW" &&
    (
      match?.seriesMatchRole === "decision_match" ||
      Number(match?.legNumber || 1) >= Number(seriesState.scheduledRegularLegCount || 1)
    )
  );

  if (!decisiveNoShowMatch || seriesState.resolutionType !== "FORFEIT") {
    return { disqualifiedUids: [], reason: null };
  }

  const loserUid = decisiveNoShowMatch.checkInNoShowLoserUid || decisiveNoShowMatch.loserUid || seriesState.loserUid;
  return loserUid
    ? { disqualifiedUids: [loserUid], reason: "CHECK_IN_NO_SHOW_FORFEIT" }
    : { disqualifiedUids: [], reason: null };
}
